import {t} from '@lingui/macro'
import {useEffect} from 'react'

import {Modal} from '@pleo-io/telescope'

import {setStorageItem} from '@product-web/api-deimos/storage'
import {Interval} from '@product-web/api-types/billing'
import type {PlanUpgradeSource, SubscriptionUpdateSource} from '@product-web/api-types/plan'
import {PlanType} from '@product-web/api-types/plan'
import {getErrorMessage} from '@product-web/error/message'
import {reportError} from '@product-web/error/report'
import {useActionWithFeedback} from '@product-web/telescope-lab/use-action-with-feedback/use-action-with-feedback'
import {useToaster} from '@product-web/toaster'
import {getCountry, useUser} from '@product-web/user'
import {useHasAWalletLoad} from '@product-web-features/funds-management/use-has-a-wallet-load'

import tracking from '~/tracking'

import {getIsLastDay} from './choose-plan-container.helper'
import type {UpdatePlanProps} from './modal-choose-plan'
import {ChoosePlanModal} from './modal-choose-plan'

import {useEndTrialModalRequirements} from '../end-trial'
import {useCompanySubscription, useUpdateCompanySubscription} from '../lib/company-subscription'

interface ChoosePlanContainerProps {
    isOpen: boolean
    onDismiss: (didCompleteAction?: boolean) => void
    planUpgradeSource?: PlanUpgradeSource
    trackingOrigin?: string
}

export const ChoosePlanContainer = ({
    isOpen,
    planUpgradeSource,
    onDismiss,
    trackingOrigin,
}: ChoosePlanContainerProps) => {
    const user = useUser()
    const {showToast} = useToaster()

    useEffect(() => {
        if (isOpen) {
            tracking.choosePlanModalActioned({
                action: 'viewed',
                origin: trackingOrigin,
            })
        }
    }, [isOpen, trackingOrigin])

    const {data: subscription} = useCompanySubscription()
    const {mutateAsync: updateSubscription} = useUpdateCompanySubscription()

    const trialEndDate = subscription?.trialPeriod?.exclusiveEndDate
        ? new Date(subscription.trialPeriod.exclusiveEndDate)
        : undefined

    const {hasAWalletLoad} = useHasAWalletLoad()
    const {shouldShowEndTrialModal} = useEndTrialModalRequirements()

    const updatePlan = useActionWithFeedback(
        async ({
            planType,
            interval = Interval.MONTH,
            setLoading,
            contractIntervalCommitment,
        }: UpdatePlanProps) => {
            try {
                switch (planType) {
                    case PlanType.STARTER:
                        await updateSubscription({
                            ratePlanType: 'STARTER',
                            billingPeriodType: 'MONTH',
                            subscriptionUpdateSource: planUpgradeSource as SubscriptionUpdateSource,
                        })
                        break
                    case PlanType.ESSENTIAL:
                        await updateSubscription({
                            ratePlanType: 'ESSENTIAL',
                            billingPeriodType: interval,
                            subscriptionUpdateSource: planUpgradeSource as SubscriptionUpdateSource,
                        })
                        break
                    case PlanType.ADVANCED:
                        await updateSubscription({
                            ratePlanType: 'ADVANCED',
                            billingPeriodType: interval,
                            subscriptionUpdateSource: planUpgradeSource as SubscriptionUpdateSource,
                        })
                        break
                    default:
                        await updateSubscription({
                            ratePlanType: 'ESSENTIAL',
                            billingPeriodType: 'MONTH',
                            subscriptionUpdateSource: planUpgradeSource as SubscriptionUpdateSource,
                        })
                        break
                }

                if (user?.companyId) {
                    await setStorageItem({
                        key: 'pricing_plan',
                        value: planType.toLowerCase(),
                        companyId: user.companyId,
                        membership: 'member',
                        sendNotification: false,
                    })
                }

                tracking.choosePlanModalActioned({
                    action: 'completed',
                    previousPlan: subscription?.platformRatePlan?.type,
                    plan: planType,
                    interval,
                    origin: trackingOrigin,
                    commitment: contractIntervalCommitment,
                })
                onDismiss(true)
                return {status: 'success'}
            } catch (err) {
                reportError(err)
                showToast(getErrorMessage(err), {level: 'error'})
                onDismiss()
                return {status: 'cancelled'}
            } finally {
                setLoading(false)
            }
        },
        () => ({
            successMessage: t`Plan changed successfully`,
            errorMessage: t`An error occurred. Please try again later or contact support.`,
        }),
    )

    const getIsDismissible = () => {
        const hasPlan = !!subscription?.platformRatePlan?.type
        if (
            (!trialEndDate || getIsLastDay({trialEndDate})) &&
            hasPlan &&
            hasAWalletLoad &&
            shouldShowEndTrialModal
        ) {
            return false
        }

        return true
    }

    const onDismissWithTracking = (didCompleteAction?: boolean) => {
        if (!didCompleteAction) {
            tracking.choosePlanModalActioned({
                action: 'abandoned',
                origin: trackingOrigin,
            })
        }
        onDismiss(didCompleteAction)
    }

    return (
        <Modal
            style={{width: 1066, paddingTop: 0}}
            isOpen={isOpen}
            aria-labelledby="choose-plan-modal-header-text"
        >
            <ChoosePlanModal
                isDismissible={getIsDismissible()}
                trialEndDate={trialEndDate}
                onDismiss={onDismissWithTracking}
                updatePlan={updatePlan}
                hasFooter
                subscription={subscription ?? null}
                country={getCountry(user)!}
            />
        </Modal>
    )
}
